import { BarChartSquarePlusIcon } from '@langchain/untitled-ui-icons';
import { Column, Table } from '@tanstack/react-table';

import { PopoverDropdownMenu } from '@/components/PopoverDropdownMenu';
import { SessionSchema } from '@/types/schema';

import { getDisplayNameFromColumn } from '../utils/getDisplayNameFromColumn';

export function SessionsDataGridChartPicker(props: {
  table: Table<SessionSchema>;
  chartableColumns: string[];
  rowData?: SessionSchema[];
  allAccessorFns: Record<string, (row: SessionSchema) => string>;
  onChange: (id: string[]) => void;
  selectedColumnIds?: string[];
}) {
  const mapChartableColumnToNumberOfNonnullSessions = new Map<string, number>();
  props.chartableColumns.forEach((columnId) => {
    const countNumberOfRowsWithNonnullValueForThisCOlumn =
      props.rowData?.filter((row) => {
        const accessorFn = props.allAccessorFns[columnId];
        if (!accessorFn) {
          return false;
        }
        return accessorFn(row) != null;
      }).length;
    countNumberOfRowsWithNonnullValueForThisCOlumn != null &&
      mapChartableColumnToNumberOfNonnullSessions.set(
        columnId,
        countNumberOfRowsWithNonnullValueForThisCOlumn
      );
  });
  const chartableColumnsToDisplay = props.table
    .getAllLeafColumns()
    .filter(
      (column) =>
        (mapChartableColumnToNumberOfNonnullSessions?.get(column.id) ?? 0) > 0
    );

  return (
    <PopoverDropdownMenu
      values={chartableColumnsToDisplay}
      onSelect={(col) => {
        if (props.selectedColumnIds?.includes(col.id)) {
          props.onChange(props.selectedColumnIds.filter((id) => id !== col.id));
        } else {
          props.onChange([...(props.selectedColumnIds ?? []), col.id]);
        }
      }}
      getTitle={(col) => {
        return getNameFromColumn(col);
      }}
      getIsSelected={(col) =>
        props.selectedColumnIds?.includes(col.id) ?? false
      }
      getSubtitle={(col) => {
        const numberOfNonnullColumns =
          mapChartableColumnToNumberOfNonnullSessions.get(col.id);
        return `${numberOfNonnullColumns?.toString() ?? '0'} ${
          numberOfNonnullColumns === 1 ? 'experiment' : 'experiments'
        }`;
      }}
      enableSearch
      getSection={(col) => {
        return col.id?.startsWith('feedback_stats_')
          ? 'Evaluation Scores'
          : col.id?.startsWith('session_feedback_stats_')
          ? 'Experiment Evaluation Scores'
          : col.id?.startsWith('latency') || col.id === 'error_rate'
          ? 'Performance'
          : col.id?.includes('tokens') || col.id?.includes('cost')
          ? 'Cost'
          : 'Other';
      }}
      sectionsOrder={[
        'Experiment Evaluation Scores',
        'Evaluation Scores',
        'Performance',
        'Cost',
        'Other',
      ]}
      stayOpenOnSelect
    >
      <div className="flex items-center gap-2 rounded-md border border-secondary px-2 py-1 text-xs transition-colors hover:bg-secondary focus:outline-none">
        <BarChartSquarePlusIcon className="h-4 w-4" />
        <div className="inline-flex items-center gap-1 font-medium">Charts</div>
      </div>
    </PopoverDropdownMenu>
  );
}

const getNameFromColumn = (column: Column<SessionSchema, unknown>): string => {
  if (column.id?.startsWith('feedback_stats_')) {
    return (
      column.id
        ?.replace('feedback_stats_', '')
        ?.replace(/\b\w/g, (char) => char.toUpperCase()) ?? ''
    );
  } else if (column.id?.startsWith('session_feedback_stats')) {
    return (
      column.id
        ?.replace('session_feedback_stats_', '')
        ?.replace(/\b\w/g, (char) => char.toUpperCase()) ?? ''
    );
  }
  return getDisplayNameFromColumn(column);
};
